import { Box, Paper, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  EmployeeActivityType,
  EmployeeWorkSummary,
  SystemServiceType,
} from '../../types';

export function EmployeeWorkTotals({
  summary,
  serviceType,
}: {
  summary?: EmployeeWorkSummary;
  serviceType: SystemServiceType;
}) {
  const { classes } = useStyles();
  const activities = summary?.activities ?? [];
  const expectedActivities = expectedActivitiesByServiceType[serviceType];
  const breakdown = expectedActivities
    ? expectedActivities.map(([activityType, label]) => {
        const recorded = activities.find(
          (a) => a.activityType === activityType,
        );
        return {
          activityType,
          activityTypeDisplay: label,
          totalDurationDisplay: recorded?.totalDurationDisplay ?? '--',
        };
      })
    : activities;
  const otherActivities = activities.filter(
    (a) => !breakdown.some((b) => b.activityType === a.activityType),
  );
  const allItems = [...breakdown, ...otherActivities];
  return (
    <Paper variant='outlined' sx={{ p: '16px 18px', mb: 3 }}>
      <Box className={classes.header}>
        <Typography component='h2' className={classes.heading}>
          Employee work time
        </Typography>
        <Box className={classes.totals}>
          <Box className={classes.totalItem}>
            <Typography className={classes.label}>Total time spent</Typography>
            <Typography className={classes.totalValue}>
              {summary?.totalDurationDisplay ?? '--'}
            </Typography>
          </Box>
          <Box className={classes.totalItem}>
            <Typography className={classes.label}>
              Total employees involved
            </Typography>
            <Typography className={classes.totalValue}>
              {summary?.employeeCount ?? '--'}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box className={classes.grid}>
        {allItems.map((activity) => {
          const hasValue = activity.totalDurationDisplay !== '--';
          return (
            <Box key={activity.activityType} className={classes.gridCell}>
              <Typography className={classes.label}>
                {activity.activityTypeDisplay}
              </Typography>
              <Typography
                className={hasValue ? classes.activeValue : classes.emptyValue}
              >
                {activity.totalDurationDisplay}
              </Typography>
            </Box>
          );
        })}
        {summary?.activities.length === 0 && (
          <Typography variant='body2' color='text.secondary'>
            No employee work recorded.
          </Typography>
        )}
      </Box>
      {/* {!!summary?.employees?.length && (
        <>
          <Typography component='h3' className={classes.subheading}>
            Time by employee
          </Typography>
          <Box className={classes.grid}>
            {summary.employees.map((employee) => (
              <Box key={employee.userID} className={classes.gridCell}>
                <Typography className={classes.label}>
                  {employee.name ?? '--'}
                </Typography>
                <Typography className={classes.activeValue}>
                  {employee.totalDurationDisplay}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      )} */}
    </Paper>
  );
}

// Activity types each service records, so a category shows even before any time is logged
const manualActivity: [EmployeeActivityType, string] = [
  'ManualActivity',
  'Time for manual activities',
];
const expectedActivitiesByServiceType: Partial<
  Record<SystemServiceType, Array<[EmployeeActivityType, string]>>
> = {
  OrderFulfillment: [
    ['OrderPick', 'Time to pick'],
    ['OrderProcessing', 'Time to process'],
    ['PalletStoring', 'Time to store pallets'],
    ['PalletPulling', 'Time to pull pallets'],
    ['PalletDispatching', 'Time to dispatch pallets'],
    manualActivity,
  ],
  Receipt: [
    ['PrepReceipts', 'Time to prep'],
    ['TransportReceipts', 'Time to transport'],
    ['PutAwayReceipts', 'Time to put away'],
    manualActivity,
  ],
  Appointment: [['Unload', 'Time to unload'], manualActivity],
  OrderPick: [['OrderPick', 'Time to pick']],
  DrayageJob: [manualActivity],
  TruckingJob: [manualActivity],
};

const useStyles = makeStyles({ name: { EmployeeWorkTotals } })((theme) => ({
  header: {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: 18,
  },
  heading: {
    fontFamily: 'Montserrat',
    fontSize: 14,
    fontWeight: 700,
    marginRight: 'auto',
  },
  subheading: {
    fontFamily: 'Montserrat',
    fontSize: 12,
    fontWeight: 700,
    marginTop: 16,
  },
  label: {
    fontSize: 9.5,
    color: theme.palette.text.secondary,
  },
  totals: {
    display: 'flex',
    alignItems: 'baseline',
    flexWrap: 'wrap',
    gap: 18,
  },
  totalItem: {
    display: 'flex',
    alignItems: 'baseline',
    gap: 6,
  },
  totalValue: {
    fontSize: 15,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
  },
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    marginTop: 12,
    borderTop: `1px solid ${theme.palette.divider}`,
  },
  gridCell: {
    padding: '10px 16px 10px 0',
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:not(:nth-of-type(3n+1))': {
      borderLeft: `1px solid ${theme.palette.divider}`,
      paddingLeft: 16,
    },
    // Only the last row drops its divider: its first cell plus any cells after it
    '&:nth-of-type(3n+1):nth-last-of-type(-n+3), &:nth-of-type(3n+1):nth-last-of-type(-n+3) ~ &':
      {
        borderBottom: 0,
      },
  },
  activeValue: {
    fontSize: 13.5,
    fontWeight: 700,
    fontVariantNumeric: 'tabular-nums',
    marginTop: 1,
    color: theme.palette.primary.main,
  },
  emptyValue: {
    fontSize: 13.5,
    fontWeight: 400,
    fontVariantNumeric: 'tabular-nums',
    marginTop: 1,
    color: theme.palette.text.disabled,
  },
}));
